import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { filterClasses } from "@swivl-mono/helpers";
import { NodeKey } from "lexical";
import type { JSX, MouseEvent } from "react";

interface VariablePillProps {
  nodeKey: NodeKey;
  id: string;
  display: string;
  /** The inline fallback (`{{Type:Key|"literal"}}`): shown after the name so the author sees what is said when the value is missing. */
  fallback?: string | null;
}

/** The visual for a VariableNode: display name (plus its fallback) — the raw `{{Type:Key}}` is never shown to the person typing. */
export function VariablePill({ nodeKey, id, display, fallback = null }: VariablePillProps): JSX.Element {
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);

  const handleClick = (event: MouseEvent<HTMLSpanElement>): void => {
    event.preventDefault();
    if (event.shiftKey) {
      setSelected(!isSelected);
      return;
    }
    clearSelection();
    setSelected(true);
  };

  return (
    <span
      role="button"
      tabIndex={-1}
      contentEditable={false}
      data-variable-id={id}
      title={fallback === null ? id : `${id} — or "${fallback}" when empty`}
      onClick={handleClick}
      className={filterClasses(
        "inline-flex cursor-default items-center rounded-md border px-1.5 py-0.5 align-baseline text-xs leading-tight font-medium whitespace-nowrap select-none",
        isSelected ? "border-orange-500 bg-orange-100 text-orange-800 ring-2 ring-orange-200" : "border-orange-200 bg-orange-50 text-orange-700"
      )}
    >
      {display}
      {fallback !== null && <span className="ml-1 font-normal opacity-70">or &ldquo;{fallback}&rdquo;</span>}
    </span>
  );
}
